import { useCompanies, useSiteSettings, usePageBySlug, usePageSections, useRecordClickOut, usePublishedBlogPosts, useActiveAdverts } from "@/lib/api";
import { usePageTracking } from "@/hooks/usePageTracking";
import { Link } from "wouter";
import { Header } from "@/components/Header";
import { CompanyCard } from "@/components/CompanyCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Clock, ArrowUpDown, LayoutGrid, Table, Facebook, Twitter, Instagram, Linkedin, Youtube, Phone, Mail, MapPin, Star, Quote, CheckCircle, PoundSterling, Timer, Filter, X, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { PageSection } from "@shared/schema";
import { useState, useMemo, useEffect, useRef, lazy, Suspense } from "react";

const ComparisonTable = lazy(() => import("@/components/ComparisonTable").then(m => ({ default: m.ComparisonTable })));
import heroBannerImage from "@assets/stock_images/broken_down_vehicle_hero.webp";
import { optimizedImageUrl } from "@/lib/imageUrl";

type SortOption = "price-asc" | "price-desc" | "rating" | "name";

function getBlockContent(section: PageSection): Record<string, unknown> {
  if (!section.content) return {};
  if (typeof section.content === "string") {
    try {
      return JSON.parse(section.content);
    } catch {
      return {};
    }
  }
  return section.content as Record<string, unknown>;
}

function HomepageSectionRenderer({ section }: { section: PageSection }) {
  if (!section.isVisible) return null;
  
  const content = getBlockContent(section);
  
  switch (section.sectionType) {
    case "text": {
      const textContent = typeof section.content === "string" ? section.content : String(section.content || "");
      return (
        <div className="py-8">
          {section.heading && <h2 className="font-display font-semibold text-2xl mb-4">{section.heading}</h2>}
          {textContent && <div className="text-muted-foreground whitespace-pre-wrap">{textContent}</div>}
        </div>
      );
    }
    
    case "cta": {
      const ctaTitle = String(content.title || "");
      const ctaDesc = String(content.description || "");
      const ctaButton = String(content.buttonText || "");
      const ctaUrl = String(content.buttonUrl || "");
      return (
        <div className={`rounded-xl p-8 text-center my-8 ${content.variant === "secondary" ? "bg-secondary" : "bg-primary text-primary-foreground"}`}>
          {ctaTitle && <h3 className="font-display font-bold text-2xl mb-2">{ctaTitle}</h3>}
          {ctaDesc && <p className="mb-4 opacity-90">{ctaDesc}</p>}
          {ctaButton && ctaUrl && (
            <Button variant={content.variant === "primary" ? "secondary" : "default"} asChild>
              <a href={ctaUrl}>{ctaButton}</a>
            </Button>
          )}
        </div>
      );
    }
    
    case "feature_grid":
      const features = (content.features || []) as Array<{ title: string; description: string }>;
      const columns = (content.columns || 3) as number;
      const gridColsClass = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" }[columns] || "md:grid-cols-3";
      const featureIcons = [Shield, PoundSterling, Timer, Star, CheckCircle, Clock];
      const featureColors = [
        "from-blue-500 to-blue-600",
        "from-green-500 to-green-600", 
        "from-purple-500 to-purple-600",
        "from-orange-500 to-orange-600",
        "from-pink-500 to-pink-600",
        "from-cyan-500 to-cyan-600"
      ];
      return (
        <div className="py-12">
          {section.heading && (
            <div className="text-center mb-10">
              <h2 className="font-display font-bold text-3xl mb-3">{section.heading}</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-primary to-primary/50 mx-auto rounded-full"></div>
            </div>
          )}
          <div className={`grid grid-cols-1 ${gridColsClass} gap-8`}>
            {features.map((feature, idx) => {
              const IconComponent = featureIcons[idx % featureIcons.length];
              const colorClass = featureColors[idx % featureColors.length];
              return (
                <Card key={idx} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-md overflow-hidden">
                  <CardContent className="p-0">
                    <div className={`bg-gradient-to-br ${colorClass} p-6 flex items-center justify-center`}>
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-display font-semibold text-lg mb-2 group-hover:text-primary transition-colors">{feature.title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      );
    
    case "accordion":
      const items = (content.items || []) as Array<{ title: string; content: string }>;
      return (
        <div className="py-8">
          {section.heading && <h2 className="font-display font-semibold text-2xl mb-6">{section.heading}</h2>}
          <Accordion type="single" collapsible className="w-full">
            {items.map((item, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`}>
                <AccordionTrigger className="text-left">{item.title}</AccordionTrigger>
                <AccordionContent>{item.content}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      );
    
    case "testimonial": {
      const testQuote = String(content.quote || "");
      const testAuthor = String(content.author || "");
      const testRole = String(content.role || "");
      return (
        <div className="py-8">
          {section.heading && <h2 className="font-display font-semibold text-2xl mb-6">{section.heading}</h2>}
          <Card className="bg-muted/50">
            <CardContent className="p-8">
              <Quote className="w-10 h-10 text-primary/30 mb-4" />
              <blockquote className="text-lg italic mb-4">{testQuote}</blockquote>
              <div>
                <p className="font-semibold">{testAuthor}</p>
                {testRole && <p className="text-sm text-muted-foreground">{testRole}</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    case "stats":
      const stats = (content.stats || []) as Array<{ value: string; label: string }>;
      return (
        <div className="py-8">
          {section.heading && <h2 className="font-display font-semibold text-2xl mb-6">{section.heading}</h2>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center p-4">
                <p className="font-display font-bold text-3xl text-primary">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      );
    
    case "divider":
      return <hr className="my-8 border-border" />;
    
    default:
      return null;
  }
}

export default function Home() {
  const { data: companies = [], isLoading } = useCompanies();
  const { data: settings = {} } = useSiteSettings();
  const { data: homePage } = usePageBySlug("home");
  const { data: homeSections = [] } = usePageSections(homePage?.id || "");
  const { data: blogPosts = [] } = usePublishedBlogPosts();
  const [sortBy, setSortBy] = useState<SortOption>("price-asc");
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [coverageFilters, setCoverageFilters] = useState<{
    roadsideAssistance: boolean;
    homeStart: boolean;
    nationalRecovery: boolean;
    onwardTravel: boolean;
    europecover: boolean;
    hireCosts: boolean;
    bedAndBreakfast: boolean;
    messageRelay: boolean;
  }>({
    roadsideAssistance: false,
    homeStart: false,
    nationalRecovery: false,
    onwardTravel: false,
    europecover: false,
    hireCosts: false,
    bedAndBreakfast: false,
    messageRelay: false,
  });

  const coverageOptions = [
    { key: "roadsideAssistance" as const, label: "Roadside Assistance" },
    { key: "homeStart" as const, label: "Home Start" },
    { key: "nationalRecovery" as const, label: "National Recovery" },
    { key: "onwardTravel" as const, label: "Onward Travel" },
    { key: "europecover" as const, label: "European Cover" },
    { key: "hireCosts" as const, label: "Hire Costs" },
    { key: "bedAndBreakfast" as const, label: "Bed & Breakfast" },
    { key: "messageRelay" as const, label: "Message Relay" },
  ];

  const activeFilterCount = Object.values(coverageFilters).filter(Boolean).length;
  const recordClickOut = useRecordClickOut();
  const clickInIdRef = usePageTracking();
  const { data: activeAdverts = [] } = useActiveAdverts("provider-cards");

  // Handle hash anchor scrolling on page load
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const scrollToElement = () => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          return true;
        }
        return false;
      };
      
      // Try immediately, then retry with delays if element not found
      if (!scrollToElement()) {
        const attempts = [100, 300, 500, 1000];
        attempts.forEach(delay => {
          setTimeout(scrollToElement, delay);
        });
      }
    }
  }, [isLoading]);

  const lastClickOutRef = useRef<{ companyId: string; time: number } | null>(null);

  const handleClickOut = (companyId: string, companyName: string, destinationUrl: string) => {
    const now = Date.now();
    if (lastClickOutRef.current && lastClickOutRef.current.companyId === companyId && now - lastClickOutRef.current.time < 5000) {
      return;
    }
    lastClickOutRef.current = { companyId, time: now };

    const visitorId = localStorage.getItem("visitorId");
    recordClickOut.mutate({
      companyId,
      companyName,
      visitorId,
      clickInId: clickInIdRef.current,
      destinationUrl,
    });
  };

  const sortedHomeSections = [...homeSections].sort((a, b) => a.sortOrder - b.sortOrder);
  
  const heroSection = sortedHomeSections.find(s => s.sectionType === "hero");
  const heroContent = heroSection ? getBlockContent(heroSection) : null;
  const heroBadge = String(heroContent?.badge ?? settings.heroBadge ?? "Compare & Save");
  const heroTitle = String(heroContent?.title ?? heroSection?.heading ?? settings.heroTitle ?? "Find the Best Breakdown Cover");
  const heroDescription = String(heroContent?.description ?? settings.heroDescription ?? "Compare prices and features from the UK's leading breakdown cover providers. Get peace of mind on the road at the best price.");
  
  const featureSection = sortedHomeSections.find(s => s.sectionType === "feature_grid");
  const featureContent = featureSection ? getBlockContent(featureSection) : null;
  const featureItems = (featureContent?.features ?? []) as Array<{ title: string; description: string }>;
  const defaultFeatures = [
    { icon: Shield, title: "Trusted providers", description: "" },
    { icon: PoundSterling, title: "Best prices", description: "" },
    { icon: Timer, title: "Quick quotes", description: "" },
  ];
  const heroFeatureIcons = [Shield, PoundSterling, Timer];
  
  const otherSections = sortedHomeSections.filter(s => s.sectionType !== "hero");
  
  const footerText = settings.footerText || "Compare breakdown cover providers and find the best deal for your needs.";

  // Parse footer settings
  const footerLinks = (() => {
    try {
      return settings.footerLinks ? JSON.parse(settings.footerLinks) : [];
    } catch { return []; }
  })();
  const socialLinks = (() => {
    try {
      return settings.socialLinks ? JSON.parse(settings.socialLinks) : {};
    } catch { return {}; }
  })();
  const companyInfo = (() => {
    try {
      return settings.companyInfo ? JSON.parse(settings.companyInfo) : {};
    } catch { return {}; }
  })();

  const sortedCompanies = useMemo(() => {
    let filtered = [...companies];
    
    if (coverageFilters.roadsideAssistance) {
      filtered = filtered.filter(c => c.roadsideAssistance);
    }
    if (coverageFilters.homeStart) {
      filtered = filtered.filter(c => c.homeStart);
    }
    if (coverageFilters.nationalRecovery) {
      filtered = filtered.filter(c => c.nationalRecovery);
    }
    if (coverageFilters.onwardTravel) {
      filtered = filtered.filter(c => c.onwardTravel);
    }
    if (coverageFilters.europecover) {
      filtered = filtered.filter(c => c.europecover);
    }
    if (coverageFilters.hireCosts) {
      filtered = filtered.filter(c => c.hireCosts);
    }
    if (coverageFilters.bedAndBreakfast) {
      filtered = filtered.filter(c => c.bedAndBreakfast);
    }
    if (coverageFilters.messageRelay) {
      filtered = filtered.filter(c => c.messageRelay);
    }
    
    switch (sortBy) {
      case "price-asc":
        return filtered.sort((a, b) => a.pricePerYear - b.pricePerYear);
      case "price-desc":
        return filtered.sort((a, b) => b.pricePerYear - a.pricePerYear);
      case "rating":
        return filtered.sort((a, b) => b.rating - a.rating);
      case "name":
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return filtered;
    }
  }, [companies, sortBy, coverageFilters]);

  const featuredCompanies = sortedCompanies.filter((c) => c.featured);
  const otherCompanies = sortedCompanies.filter((c) => !c.featured);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="relative overflow-hidden border-b">
        <img 
          src={heroBannerImage}
          alt=""
          role="presentation"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/85 to-background/70" />
        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          <div className="max-w-3xl animate-fade-in-up">
            <Badge className="mb-4 bg-green-100 text-green-800 hover:bg-green-200 border-green-200" data-testid="hero-badge">
              {heroBadge}
            </Badge>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1]" data-testid="hero-title">
              {heroTitle.includes("Breakdown Cover") ? (
                <>
                  {heroTitle.replace("Breakdown Cover", "")}
                  <br className="hidden sm:block" />
                  <span className="text-primary">Breakdown Cover</span>
                </>
              ) : heroTitle}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-xl" data-testid="hero-description">
              {heroDescription}
            </p>

            <div className="flex flex-wrap gap-6 mt-8">
              {featureItems.length > 0 ? (
                featureItems.slice(0, 3).map((feature, idx) => {
                  const IconComponent = heroFeatureIcons[idx % heroFeatureIcons.length];
                  return (
                    <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <IconComponent className="w-4 h-4 text-primary" />
                      <span>{feature.title}</span>
                    </div>
                  );
                })
              ) : (
                defaultFeatures.map(({ icon: Icon, title }) => (
                  <div key={title} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon className="w-4 h-4 text-primary" />
                    <span>{title}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-muted/30 border-b overflow-hidden">
        <div className="container mx-auto px-4">
          <p className="text-center text-muted-foreground mb-6">
            We work with the UK's best breakdown cover providers
          </p>
        </div>
        <div className="relative overflow-hidden">
          <div className="flex animate-scroll gap-8 md:gap-12 pl-8">
            {[...companies, ...companies, ...companies].map((company, idx) => (
              <div 
                key={`${company.id}-${idx}`} 
                className="w-24 h-24 md:w-28 md:h-28 rounded-xl bg-card border shadow-sm flex items-center justify-center shrink-0 overflow-hidden transition-all duration-300"
                title={company.name}
              >
                {company.logoUrl ? (
                  <img 
                    src={company.logoUrl} 
                    alt={`${company.name} logo`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain p-3"
                  />
                ) : (
                  <span className="font-display font-bold text-primary text-lg">
                    {company.logo}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 py-8 md:py-12">
        {isLoading ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Loading providers...</p>
          </div>
        ) : (
          <>
        <div id="compare-providers" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 scroll-mt-4">
          <div>
            <p className="font-display font-semibold text-2xl">
              Compare vehicle breakdown cover packages
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div role="group" aria-label="View mode" className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
              <button
                type="button"
                onClick={() => setViewMode("cards")}
                aria-pressed={viewMode === "cards"}
                aria-label="Card view"
                data-testid="view-cards"
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium gap-1.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${viewMode === "cards" ? "bg-background text-foreground shadow" : ""}`}
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                aria-pressed={viewMode === "table"}
                aria-label="Table view"
                data-testid="view-table"
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium gap-1.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${viewMode === "table" ? "bg-background text-foreground shadow" : ""}`}
              >
                <Table className="w-4 h-4" />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2" aria-label="Filter providers" data-testid="filter-btn">
                  <Filter className="w-4 h-4" />
                  <span className="hidden sm:inline">Filter</span>
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="end">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Coverage Options</h4>
                    {activeFilterCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-auto p-1 text-xs"
                        onClick={() => setCoverageFilters({
                          roadsideAssistance: false,
                          homeStart: false,
                          nationalRecovery: false,
                          onwardTravel: false,
                          europecover: false,
                          hireCosts: false,
                          bedAndBreakfast: false,
                          messageRelay: false,
                        })}
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {coverageOptions.map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2">
                        <Checkbox 
                          id={key}
                          checked={coverageFilters[key]}
                          onCheckedChange={(checked) => 
                            setCoverageFilters(prev => ({ ...prev, [key]: !!checked }))
                          }
                          data-testid={`filter-${key}`}
                        />
                        <label 
                          htmlFor={key} 
                          className="text-sm cursor-pointer"
                        >
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                <SelectTrigger className="w-[180px]" aria-label="Sort providers" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {viewMode === "table" ? (
          <div className="rounded-xl border bg-card overflow-hidden">
            <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading comparison...</div>}>
              <ComparisonTable companies={sortedCompanies} />
            </Suspense>
          </div>
        ) : (
          <>
            {featuredCompanies.length > 0 && (
              <section className="mb-12">
                <h3 className="font-display font-medium text-lg mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  Featured Providers
                </h3>
                <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
                  {featuredCompanies.map((company, index) => (
                    <CompanyCard key={company.id} company={company} index={index} onClickOut={handleClickOut} />
                  ))}
                </div>
              </section>
            )}

            {activeAdverts.length > 0 && featuredCompanies.length > 0 && (
              <section className="mb-12 block md:hidden">
                <Card className="overflow-hidden border-dashed">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Other services we think might be of interest</p>
                    <div className="space-y-3">
                      {activeAdverts.map((ad) => (
                        <a
                          key={ad.id}
                          href={ad.destinationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                          data-testid={`advert-banner-${ad.id}`}
                        >
                          <img
                            src={ad.imageUrl}
                            alt={ad.title}
                            loading="lazy"
                            decoding="async"
                            className="w-full rounded-lg"
                          />
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {otherCompanies.length > 0 && (
              <section>
                {featuredCompanies.length > 0 && (
                  <h3 className="font-display font-medium text-lg mb-4">All Providers</h3>
                )}
                <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
                  {otherCompanies.map((company, index) => (
                    <CompanyCard
                      key={company.id}
                      company={company}
                      index={index + featuredCompanies.length}
                      onClickOut={handleClickOut}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {companies.length === 0 && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No providers added yet.</p>
            <Button className="mt-4" asChild>
              <a href="/admin">Add your first provider</a>
            </Button>
          </div>
        )}

        {otherSections.length > 0 && (
          <div className="mt-12 space-y-4">
            {otherSections.map((section) => (
              <HomepageSectionRenderer key={section.id} section={section} />
            ))}
          </div>
        )}

        {/* Latest Blog Posts Section */}
        {blogPosts.length > 0 && (
          <section className="mt-16 mb-8" data-testid="latest-blog-section">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-display font-bold text-2xl md:text-3xl" data-testid="blog-section-title">
                Latest from Our Blog
              </h2>
              <Link href="/blog" className="text-primary hover:underline text-sm font-medium" data-testid="view-all-blog-link">
                View All Posts →
              </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {blogPosts.slice(0, 3).map((post) => (
                <Link key={post.id} href={`/blog/${post.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group" data-testid={`blog-card-${post.id}`}>
                    {post.featuredImage && (
                      <div className="aspect-video overflow-hidden rounded-t-lg">
                        <img 
                          src={optimizedImageUrl(post.featuredImage, 600)} 
                          alt={post.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardContent className={post.featuredImage ? "pt-4" : "pt-6"}>
                      <h3 className="font-display font-semibold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2" data-testid={`blog-title-${post.id}`}>
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-muted-foreground text-sm line-clamp-3 mb-3" data-testid={`blog-excerpt-${post.id}`}>
                          {post.excerpt}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        {post.authorName && (
                          <span data-testid={`blog-author-${post.id}`}>By {post.authorName}</span>
                        )}
                        {post.publishedAt && (
                          <span data-testid={`blog-date-${post.id}`}>
                            {new Date(post.publishedAt).toLocaleDateString('en-GB', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
          </>
        )}
      </main>

      <footer className="border-t py-12 mt-12 bg-muted/30" data-testid="site-footer">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Company Info */}
            <div className="space-y-4" data-testid="footer-company-info">
              {companyInfo.name && (
                companyInfo.externalLinkUrl ? (
                  <a href={companyInfo.externalLinkUrl} target="_blank" rel="noopener noreferrer" className="font-display font-semibold text-lg hover:text-primary transition-colors" data-testid="footer-company-name">
                    {companyInfo.name}
                  </a>
                ) : (
                  <h3 className="font-display font-semibold text-lg" data-testid="footer-company-name">{companyInfo.name}</h3>
                )
              )}
              {footerText && (
                <p className="text-sm text-muted-foreground" data-testid="footer-description">{footerText}</p>
              )}
              {companyInfo.address && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="footer-address">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{companyInfo.address}</span>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-4" data-testid="footer-contact">
              <h3 className="font-display font-semibold text-lg">Contact</h3>
              {companyInfo.phone && (
                <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-phone">
                  <Phone className="w-4 h-4" />
                  <span>{companyInfo.phone}</span>
                </a>
              )}
              {companyInfo.email && (
                <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-email">
                  <Mail className="w-4 h-4" />
                  <span>{companyInfo.email}</span>
                </a>
              )}
              {companyInfo.externalLinkUrl && companyInfo.externalLinkLabel && (
                <a 
                  href={companyInfo.externalLinkUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" 
                  data-testid="footer-external-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>{companyInfo.externalLinkLabel}</span>
                </a>
              )}
              <a href="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-contact-link">
                Contact Us
              </a>
            </div>

            {/* Quick Links */}
            {footerLinks.length > 0 && (
              <div className="space-y-4" data-testid="footer-quick-links">
                <h3 className="font-display font-semibold text-lg">Quick Links</h3>
                <ul className="space-y-2">
                  {footerLinks.map((link: { id: string; label: string; url: string }) => (
                    <li key={link.id}>
                      <a href={link.url} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid={`footer-link-${link.id}`}>
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Legal Links */}
            <div className="space-y-4" data-testid="footer-legal-links">
              <h3 className="font-display font-semibold text-lg">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy-link">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms-and-conditions" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-terms-link">
                    Terms and Conditions
                  </a>
                </li>
              </ul>
            </div>

            {/* Social Links */}
            {(socialLinks.facebook || socialLinks.twitter || socialLinks.instagram || socialLinks.linkedin || socialLinks.youtube) && (
              <div className="space-y-4" data-testid="footer-social-links">
                <h3 className="font-display font-semibold text-lg">Follow Us</h3>
                <div className="flex gap-3">
                  {socialLinks.facebook && (
                    <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-background rounded-full hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="Facebook" data-testid="social-facebook">
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.twitter && (
                    <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-background rounded-full hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="Twitter" data-testid="social-twitter">
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.instagram && (
                    <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-background rounded-full hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="Instagram" data-testid="social-instagram">
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.linkedin && (
                    <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-background rounded-full hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="LinkedIn" data-testid="social-linkedin">
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.youtube && (
                    <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="p-2 bg-background rounded-full hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="YouTube" data-testid="social-youtube">
                      <Youtube className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground" data-testid="footer-copyright">
            <div>{companyInfo.copyright || `© ${new Date().getFullYear()} Top Breakdown Cover. All rights reserved.`}</div>
            <a href="/login" className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors mt-2 inline-block" data-testid="footer-admin-link">Admin</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
